import { Combobox } from "@/components/shadcn-custom/ShadcnCombobox";
import { CustomTooltip } from "@/components/shadcn-custom/ShadcnTooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { TooltipProvider } from "@/components/ui/tooltip";
import { createFileRoute } from "@tanstack/react-router";

// Helper function to trigger native date/time picker
const triggerPicker = (elementId: string) => {
  const element = document.getElementById(elementId) as HTMLInputElement | null;
  if (element && 'showPicker' in element) {
    (element as any).showPicker();
  }
};
import { ArrowUpDown, Plus, Search } from "lucide-react";
import { useState, useMemo } from "react";
import { useTasks } from "@/features/tasks/hooks/useTasks";
import { useUsers } from "@/features/users/hooks/useUsers";
import { isTaskOverdue, type TaskModel } from "@/firestore_types";
import { TaskDialog, type TaskFormData } from "@/features/tasks/components/TaskDialog";
import { TaskDetailsDialog } from "@/features/tasks/components/TaskDetailsDialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/DialogProvider";
import { formatDateTimeShort, timestampToDateString } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/tasks/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { tasks, loading, createTask, updateTask, deleteTask } = useTasks({ projectId });
  const { users } = useUsers();
  const confirm = useConfirm();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskModel | undefined>(undefined);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskModel | undefined>(undefined);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [taskFilterOpen, setTaskFilterOpen] = useState(false);
  const [taskFilters, setTaskFilters] = useState({
    status: "",
    assignee: "",
    dueDate: "",
  });

  // Filter and sort tasks (Done status at the bottom)
  const filteredTasks = useMemo(() => {
    const filtered = tasks.filter((task) => {
      // Search filter
      if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Status filter
      if (taskFilters.status) {
        const statusMap: Record<string, string> = {
          "To do": "todo",
          "In progress": "in-progress",
          "Done": "done",
        };
        if (statusMap[taskFilters.status] !== task.status) return false;
      }

      // Assignee filter
      if (taskFilters.assignee && !task.assigneeIds.includes(taskFilters.assignee)) {
        return false;
      }

      // Due date filter (compare in EST timezone)
      if (taskFilters.dueDate) {
        if (!task.dueDate) return false;
        const taskDueDateStr = timestampToDateString(task.dueDate);
        if (taskDueDateStr !== taskFilters.dueDate) {
          return false;
        }
      }

      return true;
    });

    // Sort: Done tasks go to the bottom
    return filtered.sort((a, b) => {
      if (a.status === "done" && b.status !== "done") return 1;
      if (a.status !== "done" && b.status === "done") return -1;
      return 0;
    });
  }, [tasks, searchQuery, taskFilters]);

  const hasActiveTaskFilters = taskFilters.status || taskFilters.assignee || taskFilters.dueDate;

  const getTaskStatusClass = (status: string) => {
    switch (status) {
      case "todo":
        return "bg-white text-blue-500 border-blue-500";
      case "in-progress":
        return "bg-white text-purple-600 border-purple-600";
      case "done":
        return "bg-white text-green-600 border-green-600";
      default:
        return "bg-white text-gray-600 border-gray-600";
    }
  };

  const getTaskStatusLabel = (status: string) => {
    switch (status) {
      case "todo":
        return "To do";
      case "in-progress":
        return "In progress";
      case "done":
        return "Done";
      default:
        return status;
    }
  };

  // Get unique values for task filter dropdowns
  const uniqueTaskStatuses = useMemo(() => {
    return [
      { value: "To do", label: "To do" },
      { value: "In progress", label: "In progress" },
      { value: "Done", label: "Done" },
    ];
  }, []);

  const uniqueAssignees = useMemo(() => {
    const eligibleUsers = users.filter((user) => user.type === "admin" || user.type === "manager");
    return eligibleUsers.map((user) => ({
      value: user.uid,
      label: user.name || user.email,
    }));
  }, [users]);

  // Handlers
  const handleCreate = () => {
    setEditingTask(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (task: TaskModel) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleDelete = async (task: TaskModel) => {
    const confirmed = await confirm({
      title: "Delete Task",
      description: `Are you sure you want to delete "${task.title}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (confirmed) {
      try {
        await deleteTask(task.id);
        toast.success("Task deleted successfully");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to delete task");
      }
    }
  };

  const handleSubmit = async (data: TaskFormData) => {
    try {
      if (editingTask) {
        await updateTask(editingTask.id, data);
        toast.success("Task updated successfully");
      } else {
        await createTask(data);
        toast.success("Task created successfully");
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save task");
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Timeline data matching the screenshot

  return (
    <div className="flex flex-col">
      {/* Dialogs */}
      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={editingTask}
        defaultProjectId={projectId}
        onSubmit={handleSubmit}
      />
      {selectedTask && (
        <TaskDetailsDialog
          open={detailsDialogOpen}
          onOpenChange={setDetailsDialogOpen}
          task={selectedTask}
          onEdit={() => {
            setDetailsDialogOpen(false);
            setEditingTask(selectedTask);
            setDialogOpen(true);
          }}
          onDelete={() => {
            setDetailsDialogOpen(false);
            handleDelete(selectedTask);
          }}
        />
      )}
      {/* Tab Content */}
      <div className="mt-4">
        {true === true && (
          <TooltipProvider>
            <div className="rounded border border-gray-200 bg-white">
              {/* Search and Filter Row */}
              <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 min-[744px]:flex-row min-[744px]:items-center min-[744px]:justify-between">
                <div className="relative flex-1 min-[744px]:max-w-sm">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
                  <Input
                    placeholder="Search"
                    className="h-9 w-full border-gray-300 pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex w-full items-center gap-2 min-[744px]:w-auto">
                  <button
                    onClick={() => setTaskFilterOpen(!taskFilterOpen)}
                    className="border-muted-foreground text-muted-foreground flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors hover:bg-gray-50 min-[744px]:flex-none"
                  >
                    {taskFilterOpen ? (
                      <>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.4415 2.5H2.49905C2.33789 2.50006 2.1802 2.54685 2.04509 2.6347C1.90998 2.72255 1.80324 2.84769 1.7378 2.99497C1.67237 3.14225 1.65104 3.30534 1.6764 3.46449C1.70177 3.62364 1.77274 3.77203 1.88072 3.89167L7.90155 10.5492C8.17866 10.8554 8.33219 11.2537 8.33238 11.6667V16.6667C8.33231 16.8215 8.37539 16.9733 8.45679 17.1051C8.53819 17.2368 8.65469 17.3433 8.79322 17.4125L10.4599 18.2458C10.587 18.3093 10.7282 18.3393 10.8701 18.3329C11.012 18.3264 11.1499 18.2838 11.2707 18.2091C11.3916 18.1344 11.4913 18.03 11.5604 17.9059C11.6295 17.7818 11.6658 17.6421 11.6657 17.5V11.6667C11.6659 11.2537 11.8194 10.8554 12.0965 10.5492L12.4524 10.155M13.7491 2.91667L17.9158 7.08333M17.9158 2.91667L13.7491 7.08333"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Hide filter
                      </>
                    ) : (
                      <>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.33336 16.6667C8.33329 16.8215 8.37637 16.9733 8.45777 17.1051C8.53917 17.2368 8.65566 17.3433 8.79419 17.4125L10.4609 18.2458C10.5879 18.3093 10.7291 18.3393 10.8711 18.3329C11.013 18.3264 11.1509 18.2838 11.2717 18.2091C11.3925 18.1344 11.4922 18.03 11.5614 17.9059C11.6305 17.7818 11.6668 17.6421 11.6667 17.5V11.6667C11.6669 11.2537 11.8204 10.8554 12.0975 10.5492L18.1167 3.89167C18.2246 3.77213 18.2955 3.6239 18.3209 3.4649C18.3464 3.3059 18.3252 3.14294 18.2599 2.99573C18.1947 2.84851 18.0882 2.72335 17.9534 2.63538C17.8185 2.5474 17.661 2.50038 17.5 2.5H2.50003C2.33886 2.50006 2.18118 2.54685 2.04607 2.6347C1.91096 2.72255 1.80422 2.84769 1.73878 2.99497C1.67334 3.14225 1.65201 3.30534 1.67738 3.46449C1.70274 3.62364 1.77371 3.77203 1.88169 3.89167L7.90252 10.5492C8.17964 10.8554 8.33317 11.2537 8.33336 11.6667V16.6667Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Filter
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCreate}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-purple-600 bg-white px-4 py-2.5 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-50 min-[744px]:flex-none"
                  >
                    <Plus className="h-5 w-5" />
                    Add task
                  </button>
                </div>
              </div>

              {/* Filter Panel */}
              {taskFilterOpen && (
                <div className="p-2.5 md:p-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-6">
                    <div className="grid grid-cols-1 gap-4 min-[744px]:grid-cols-3">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                        <Combobox
                          value={taskFilters.status}
                          onChange={(value) => setTaskFilters({ ...taskFilters, status: value })}
                          options={uniqueTaskStatuses}
                          placeholder="Select status"
                          searchPlaceholder="Search status..."
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Assignees
                        </label>
                        <Combobox
                          value={taskFilters.assignee}
                          onChange={(value) => setTaskFilters({ ...taskFilters, assignee: value })}
                          options={uniqueAssignees}
                          placeholder="Select assignee"
                          searchPlaceholder="Search assignee..."
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Due Date
                        </label>
                        <div className="relative">
                          <input
                            id="task-due-date-filter"
                            type="date"
                            className={`w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-inner-spin-button]:hidden ${!taskFilters.dueDate ? '[&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0' : ''}`}
                            value={taskFilters.dueDate}
                            onChange={(e) =>
                              setTaskFilters({ ...taskFilters, dueDate: e.target.value })
                            }
                          />
                          {!taskFilters.dueDate && (
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                              Select date
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => triggerPicker('task-due-date-filter')}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 ${taskFilters.dueDate ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-row gap-2 justify-end">
                      <Button
                        variant="outlineDestructive"
                        onClick={() => setTaskFilters({ status: "", assignee: "", dueDate: "" })}
                        className="flex-1 md:flex-none"
                      >
                        Reset
                      </Button>
                      <Button
                        variant="outlinePrimary"
                        onClick={() => setTaskFilterOpen(false)}
                        className="flex-1 md:flex-none"
                      >
                        Apply filter
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Filters Display */}
              {!taskFilterOpen && hasActiveTaskFilters && (
                <div className="p-2.5 md:p-4">
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    {/* Filtered by row */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-900">Filtered by:</span>
                      {taskFilters.status && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
                          {taskFilters.status}
                          <button
                            onClick={() => setTaskFilters({ ...taskFilters, status: "" })}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </span>
                      )}
                      {taskFilters.assignee && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
                          {uniqueAssignees.find((a) => a.value === taskFilters.assignee)?.label ||
                            taskFilters.assignee}
                          <button
                            onClick={() => setTaskFilters({ ...taskFilters, assignee: "" })}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </span>
                      )}
                      {taskFilters.dueDate && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
                          {taskFilters.dueDate}
                          <button
                            onClick={() => setTaskFilters({ ...taskFilters, dueDate: "" })}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </span>
                      )}
                    </div>

                    {/* Results found row */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        Results found: <span className="font-semibold">{filteredTasks.length}</span>
                      </span>
                      <button
                        onClick={() => setTaskFilters({ status: "", assignee: "", dueDate: "" })}
                        className="text-muted-foreground flex items-center gap-1.5 text-sm hover:text-gray-700"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.4415 2.5H2.49905C2.33789 2.50006 2.1802 2.54685 2.04509 2.6347C1.90998 2.72255 1.80324 2.84769 1.7378 2.99497C1.67237 3.14225 1.65104 3.30534 1.6764 3.46449C1.70177 3.62364 1.77274 3.77203 1.88072 3.89167L7.90155 10.5492C8.17866 10.8554 8.33219 11.2537 8.33238 11.6667V16.6667C8.33231 16.8215 8.37539 16.9733 8.45679 17.1051C8.53819 17.2368 8.65469 17.3433 8.79322 17.4125L10.4599 18.2458C10.587 18.3093 10.7282 18.3393 10.8701 18.3329C11.012 18.3264 11.1499 18.2838 11.2707 18.2091C11.3916 18.1344 11.4913 18.03 11.5604 17.9059C11.6295 17.7818 11.6658 17.6421 11.6657 17.5V11.6667C11.6659 11.2537 11.8194 10.8554 12.0965 10.5492L12.4524 10.155M13.7491 2.91667L17.9158 7.08333M17.9158 2.91667L13.7491 7.08333"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Clear filter set
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tasks Table or Empty State */}
              {filteredTasks.length === 0 ? (
                /* Empty State */
                (<div className="flex flex-col items-center justify-center py-16">
                  <svg
                    width="160"
                    height="84"
                    viewBox="0 0 160 84"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mb-6"
                  >
                    <path
                      d="M64.5723 15.7856C61.7319 15.7856 59.4294 18.0882 59.4294 20.9285C59.4294 23.7688 61.7319 26.0714 64.5723 26.0714H34.858C32.0177 26.0714 29.7151 28.3739 29.7151 31.2142C29.7151 34.0545 32.0177 36.3571 34.858 36.3571H53.1437C55.984 36.3571 58.2865 38.6596 58.2865 41.4999C58.2865 44.3403 55.984 46.6428 53.1437 46.6428H25.7151C22.8748 46.6428 20.5723 48.9453 20.5723 51.7856C20.5723 54.626 22.8748 56.9285 25.7151 56.9285H53.1437C50.3034 56.9285 48.0008 59.231 48.0008 62.0714C48.0008 64.9117 50.3034 67.2142 53.1437 67.2142H117.144C119.984 67.2142 122.287 64.9117 122.287 62.0714C122.287 59.231 119.984 56.9285 117.144 56.9285H115.715C112.875 56.9285 110.572 54.626 110.572 51.7856C110.572 48.9453 112.875 46.6428 115.715 46.6428H122.858C125.698 46.6428 128.001 44.3403 128.001 41.4999C128.001 38.6596 125.698 36.3571 122.858 36.3571H106.858C109.698 36.3571 112.001 34.0545 112.001 31.2142C112.001 28.3739 109.698 26.0714 106.858 26.0714H135.429C138.27 26.0714 140.572 23.7688 140.572 20.9285C140.572 18.0882 138.27 15.7856 135.429 15.7856H64.5723Z"
                      fill="#EDE9FE"
                    />
                    <path
                      d="M135.429 46.6428C138.27 46.6428 140.572 44.3403 140.572 41.4999C140.572 38.6596 138.27 36.3571 135.429 36.3571C132.589 36.3571 130.287 38.6596 130.287 41.4999C130.287 44.3403 132.589 46.6428 135.429 46.6428Z"
                      fill="#EDE9FE"
                    />
                    <rect
                      x="51.4277"
                      y="7.78564"
                      width="58.2857"
                      height="75.4286"
                      rx="4.57143"
                      fill="white"
                    />
                    <path
                      d="M62.8563 8.64279C62.8563 8.1694 62.4725 7.78564 61.9992 7.78564H55.9992C53.4744 7.78564 51.4277 9.83234 51.4277 12.3571V78.6428C51.4277 81.1675 53.4744 83.2142 55.9992 83.2142H105.142C107.667 83.2142 109.713 81.1675 109.713 78.6428V12.3571C109.713 9.83234 107.667 7.78564 105.142 7.78564H100.285C99.8115 7.78564 99.4277 8.1694 99.4277 8.64279C99.4277 9.11617 99.8115 9.49993 100.285 9.49993H105.142C106.72 9.49993 107.999 10.7791 107.999 12.3571V78.6428C107.999 80.2207 106.72 81.4999 105.142 81.4999H55.9992C54.4212 81.4999 53.142 80.2207 53.142 78.6428V12.3571C53.142 10.7791 54.4212 9.49993 55.9992 9.49993H61.9992C62.4725 9.49993 62.8563 9.11617 62.8563 8.64279Z"
                      fill="#7F22FE"
                    />
                    <path
                      d="M80 1.67871C82.4007 1.67884 84.4044 3.38388 84.8643 5.64941L84.9863 6.25H93.7139C94.562 6.25 95.25 6.93798 95.25 7.78613V10.0713C95.25 12.1817 93.539 13.8924 91.4287 13.8926H69.7139C67.6034 13.8925 65.8926 12.1818 65.8926 10.0713V7.78613C65.8926 6.93798 66.5806 6.25 67.4287 6.25H75.0127L75.1348 5.64941C75.5947 3.38379 77.5992 1.67871 80 1.67871Z"
                      fill="white"
                      stroke="#7F22FE"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M63.04 12.3571H58.2857C57.0234 12.3571 56 13.3806 56 14.6428V76.3571C56 77.6194 57.0234 78.6428 58.2857 78.6428H101.714C102.977 78.6428 104 77.6194 104 76.3571V66.0714C83.424 55.5063 76.9692 39.2046 76.58 16.9285H69.7143C67.6571 16.9285 65.943 16.662 64.5715 15.2144C63.698 14.3868 63.2544 13.4344 63.04 12.3571Z"
                      fill="#EDE9FE"
                    />
                    <mask id="path-6-inside-1_283_41793" fill="white">
                      <rect x="59.4277" y="59.2144" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="59.2144"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-6-inside-1_283_41793)"
                    />
                    <path
                      d="M72 63.2143H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <mask id="path-8-inside-2_283_41793" fill="white">
                      <rect x="59.4277" y="47.7856" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="47.7856"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-8-inside-2_283_41793)"
                    />
                    <path
                      d="M72 51.7857H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <mask id="path-10-inside-3_283_41793" fill="white">
                      <rect x="59.4277" y="36.3569" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="36.3569"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-10-inside-3_283_41793)"
                    />
                    <path
                      d="M72 40.3571H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <mask id="path-12-inside-4_283_41793" fill="white">
                      <rect x="59.4277" y="24.9287" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="24.9287"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-12-inside-4_283_41793)"
                    />
                    <path
                      d="M72 28.9286H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">Tasks list is empty</h3>
                  <p className="mb-4 text-sm text-gray-600">
                    Wait until there's at least one task or create one
                  </p>
                  <Button
                    variant="default"
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={handleCreate}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add task
                  </Button>
                </div>)
              ) : (
                /* Tasks Table */
                (<div className="overflow-x-auto [-ms-overflow-style:auto] [scrollbar-width:thin] [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:h-2">
                  <table className="w-full min-w-[900px]">
                    <thead className="border-b border-gray-200 bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900">
                            Title
                            <ArrowUpDown className="h-4 w-4" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900">
                            Description
                            <ArrowUpDown className="h-4 w-4" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900">
                            Status
                            <ArrowUpDown className="h-4 w-4" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900">
                            Assignees
                            <ArrowUpDown className="h-4 w-4" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900">
                            Due Date
                            <ArrowUpDown className="h-4 w-4" />
                          </button>
                        </th>
                        <th className="w-12 px-4 py-3 text-left whitespace-nowrap"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredTasks.map((task) => {
                        const taskAssignees = users.filter((user) =>
                          task.assigneeIds.includes(user.uid),
                        );
                        const overdue = isTaskOverdue(task);

                        return (
                          <tr key={task.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                              <button
                                onClick={() => {
                                  setSelectedTask(task);
                                  setDetailsDialogOpen(true);
                                }}
                                className="font-medium text-left hover:text-blue-600 hover:underline"
                              >
                                {task.title}
                              </button>
                              {overdue && (
                                <Badge
                                  variant="destructive"
                                  className="ml-2 text-xs"
                                >
                                  Overdue
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {task.description && task.description.trim() !== "" ? (
                                <CustomTooltip
                                  trigger={
                                    <span className="cursor-pointer line-clamp-2">
                                      {task.description}
                                    </span>
                                  }
                                  content={task.description}
                                  color="primary"
                                  side="top"
                                />
                              ) : (
                                <span className="text-gray-400 italic">No description</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="cursor-pointer">
                                    <Badge className={`rounded border ${getTaskStatusClass(task.status)}`}>
                                      {getTaskStatusLabel(task.status)}
                                    </Badge>
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  <DropdownMenuLabel>Change Status</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await updateTask(task.id, { status: "todo" });
                                        toast.success("Status updated to To-do");
                                      } catch (error) {
                                        toast.error(
                                          error instanceof Error
                                            ? error.message
                                            : "Failed to update status",
                                        );
                                      }
                                    }}
                                    disabled={task.status === "todo"}
                                  >
                                    <Badge variant="outline" className="mr-2 bg-white text-blue-500 border-blue-500">
                                      To do
                                    </Badge>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await updateTask(task.id, { status: "in-progress" });
                                        toast.success("Status updated to In Progress");
                                      } catch (error) {
                                        toast.error(
                                          error instanceof Error
                                            ? error.message
                                            : "Failed to update status",
                                        );
                                      }
                                    }}
                                    disabled={task.status === "in-progress"}
                                  >
                                    <Badge variant="secondary" className="mr-2 bg-white text-purple-600 border-purple-600">
                                      In progress
                                    </Badge>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await updateTask(task.id, { status: "done" });
                                        toast.success("Status updated to Done");
                                      } catch (error) {
                                        toast.error(
                                          error instanceof Error
                                            ? error.message
                                            : "Failed to update status",
                                        );
                                      }
                                    }}
                                    disabled={task.status === "done"}
                                  >
                                    <Badge variant="default" className="mr-2 bg-white text-green-600 border-green-600">
                                      Done
                                    </Badge>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                              {taskAssignees.length > 0 ? (
                                <div className="whitespace-nowrap">
                                  {taskAssignees.map((user, index) => (
                                    <span key={user.uid}>
                                      {user.name || user.email}
                                      {index < taskAssignees.length - 1 && ", "}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400">No assignees</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                              {task.dueDate ? formatDateTimeShort(task.dueDate) : (
                                <span className="text-gray-400">No due date</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="rounded p-1 hover:bg-gray-100">
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M8 8.66667C8.36819 8.66667 8.66667 8.36819 8.66667 8C8.66667 7.63181 8.36819 7.33333 8 7.33333C7.63181 7.33333 7.33333 7.63181 7.33333 8C7.33333 8.36819 7.63181 8.66667 8 8.66667Z"
                                        stroke="#475467"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M8 4.00001C8.36819 4.00001 8.66667 3.70153 8.66667 3.33334C8.66667 2.96515 8.36819 2.66667 8 2.66667C7.63181 2.66667 7.33333 2.96515 7.33333 3.33334C7.33333 3.70153 7.63181 4.00001 8 4.00001Z"
                                        stroke="#475467"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M8 13.3333C8.36819 13.3333 8.66667 13.0349 8.66667 12.6667C8.66667 12.2985 8.36819 12 8 12C7.63181 12 7.33333 12.2985 7.33333 12.6667C7.33333 13.0349 7.63181 13.3333 8 13.3333Z"
                                        stroke="#475467"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-40">
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => handleEdit(task)}
                                  >
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer text-red-600"
                                    onClick={() => handleDelete(task)}
                                  >
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>)
              )}
            </div>
          </TooltipProvider>
        )}
      </div>
    </div>
  )
}
